import React, {
  useState, useRef, useEffect, useImperativeHandle, forwardRef,
} from 'react';
import IconFont from '$/components/IconFont';
import AudioTime from '../AudioTime';
import styles from './index.module.scss';

enum AudioIconEnum {
  Default = 'voiceBold',
  Play = 'playFill',
  Pause = 'pauseFill',
}

enum AudioIconColorEnum {
  Default = 'var(--content-secondary)',
  Primary = 'var(--content-primaryHover)',
}

const AUTO_RESET_TIME = 3000; // 3秒后自动重置到默认状态

interface SimpleControllerProps {
  playing: boolean;
  currentTime: number;
  duration: number;
  showDownload?: boolean;
  onTogglePlay: () => void;
  onReset?: () => void;
  onDownload?: () => void;
}

// 定义暴露的方法接口
export interface SimpleControllerRef {
  reset: () => void;
}

const SimpleController = forwardRef<SimpleControllerRef, SimpleControllerProps>(({
  playing,
  currentTime,
  duration,
  showDownload = true,
  onTogglePlay,
  onReset,
  onDownload,
}, ref) => {
  const [iconConfig, setIconConfig] = useState<{
    icon: AudioIconEnum;
    color: AudioIconColorEnum;
  }>({
    icon: AudioIconEnum.Default,
    color: AudioIconColorEnum.Default,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const resetTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 鼠标移入
  const handleMouseEnter = () => {
    setIconConfig({
      icon: playing ? AudioIconEnum.Pause : AudioIconEnum.Play,
      color: AudioIconColorEnum.Primary,
    });
  };

  // 鼠标移出
  const handleMouseLeave = () => {
    // 如果正在播放，保持当前状态
    if (playing) return;

    // 如果暂停且有进度，保持当前状态
    if (!playing && currentTime > 0) return;

    // 否则恢复到默认状态
    setIconConfig({
      icon: AudioIconEnum.Default,
      color: AudioIconColorEnum.Default,
    });
    onReset?.();
  };

  // 点击图标
  const handleIconClick = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();

    setIconConfig({
      icon: playing ? AudioIconEnum.Play : AudioIconEnum.Pause,
      color: AudioIconColorEnum.Primary,
    });

    onTogglePlay();
  };

  // 下载音频
  const handleDownload = () => {
    onDownload?.();
  };

  const resetPlayer = () => {
    setIconConfig({
      icon: AudioIconEnum.Default,
      color: AudioIconColorEnum.Default,
    });
  };

  // 暴露方法给父组件
  useImperativeHandle(ref, () => ({
    reset: resetPlayer,
  }));

  // 播放状态变化时更新图标
  useEffect(() => {
    if (playing) {
      setIconConfig({
        icon: AudioIconEnum.Pause,
        color: AudioIconColorEnum.Primary,
      });
    }
  }, [playing]);

  // 暂停状态静止10秒后自动重置
  useEffect(() => {
    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }

    if (!playing && currentTime > 0) {
      resetTimerRef.current = setTimeout(() => {
        setIconConfig({
          icon: AudioIconEnum.Default,
          color: AudioIconColorEnum.Default,
        });
        onReset?.();
      }, AUTO_RESET_TIME);
    }

    return () => {
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
      }
    };
  }, [playing, currentTime]);

  // 是否显示时间提示
  const showTime = playing || iconConfig.icon === AudioIconEnum.Play;

  return (
    <div
      ref={containerRef}
      className={styles.simpleController}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <IconFont
        name={iconConfig.icon}
        fontSize={24}
        color={iconConfig.color}
        onClick={handleIconClick}
      />

      {showTime && containerRef.current && (
        <>
          <div className={styles.options}>
            {showDownload && (
              <IconFont
                name="downloadBold"
                fontSize={16}
                color="var(--content-secondary)"
                onClick={handleDownload}
              />
            )}
          </div>
          <AudioTime
            currentTime={currentTime}
            totalTime={duration}
            anchorRef={containerRef}
          />
        </>
      )}
    </div>
  );
});

SimpleController.displayName = 'SimpleController';

export default SimpleController;

// Hook 用法
export function useSimpleController() {
  return useRef<SimpleControllerRef>(null);
}
